import React, { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import CustomCard from "../Components/CustomCard";

const CLAMP_LINES = 4;
const TOGGLE_MIN_CHARS = 180;

const AdoptableDog = ({ limit }) => {
  const { t } = useTranslation();

  const cards = useMemo(
    () => [
      { id: "topike", imgUrls: ["/img/topike.jpg", "/img/topike1.jpg", "/img/topike2.jpg"], title: t("adopt.subtitle-18"), body: t("adopt.text-18") },
      { id: "bogyo", imgUrls: ["/img/bogyo.jpg", "/img/bogyo1.jpg", "/img/bogyo2.jpg"], title: t("adopt.subtitle-17"), body: t("adopt.text-17") },
      { id: "cezar", imgUrls: ["/img/cezar.jpg", "/img/cezar1.jpg", "/img/cezar2.jpg"], title: t("adopt.subtitle-6"), body: t("adopt.text-6") },
      { id: "ledi", imgUrl: "/img/lediandson.jpg", title: t("adopt.subtitle-7"), body: t("adopt.text-7") },
      { id: "negro", imgUrls: ["/img/negro.jpg", "/img/negro1.jpg", "/img/negro2.jpg", "/img/negro3.jpg", "/img/negro4.jpg"], title: t("adopt.subtitle-8"), body: t("adopt.text-8") },
      { id: "majzli", imgUrl: "/img/majzli.jpg", title: t("adopt.subtitle-9"), body: t("adopt.text-9") },
      { id: "mia", imgUrls: ["/img/mia.jpg", "/img/mia1.jpg"], title: t("adopt.subtitle-10"), body: t("adopt.text-10") },
      { id: "bogar", imgUrl: "/img/bogar.jpg", title: t("adopt.subtitle-11"), body: t("adopt.text-11") },
      { id: "artur", imgUrl: "/img/artur.jpg", title: t("adopt.subtitle-12"), body: t("adopt.text-12") },
      { id: "szelli", imgUrls: ["/img/szelli.jpg", "/img/szelli1.jpg", "/img/szelli2.jpg"], title: t("adopt.subtitle-13"), body: t("adopt.text-13") },
      { id: "goody", imgUrls: ["/img/goody.jpg", "/img/goody1.jpg", "/img/goody2.jpg"], title: t("adopt.subtitle-14"), body: t("adopt.text-14") },
      { id: "roxi", imgUrl: "/img/roxi.jpg", title: t("adopt.subtitle-15"), body: t("adopt.text-15") },
      { id: "hektor", imgUrl: "/img/hektor.jpg", title: t("adopt.subtitle-16"), body: t("adopt.text-16") },
      { id: "falco", imgUrl: "/img/falco.jpg", title: t("adopt.subtitle-1"), body: t("adopt.text-1") },
      { id: "bella", imgUrl: "/img/bella.jpg", title: t("adopt.subtitle-2"), body: t("adopt.text-2") },
      { id: "jack", imgUrl: "/img/jack.jpg", title: t("adopt.subtitle-5"), body: t("adopt.text-5") },
      { id: "pocok", imgUrl: "/img/pocok.jpg", title: t("adopt.subtitle-4"), body: t("adopt.text-4") },
      { id: "bobi", imgUrl: "/img/bobi.jpg", title: t("adopt.subtitle-3"), body: t("adopt.text-3") },
    ],
    [t]
  );

  const visibleCards = limit ? cards.slice(0, limit) : cards;

  const [expandedId, setExpandedId] = useState(null);
  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const refs = useRef({});
  const setRef = (id) => (el) => (refs.current[id] = el);

  const [baseMinHeight, setBaseMinHeight] = useState(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 576 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const compute = () => {
      const heights = visibleCards.map(({ id }) => refs.current[id]?.offsetHeight || 0);
      const collapsedHeights = visibleCards
        .map(({ id }, idx) => (id !== expandedId ? heights[idx] : 0))
        .filter(Boolean);
      const maxH = collapsedHeights.length ? Math.max(...collapsedHeights) : 0;
      if (maxH !== baseMinHeight) setBaseMinHeight(maxH);
    };

    const tId = setTimeout(compute, 0);
    window.addEventListener("resize", compute);
    return () => {
      clearTimeout(tId);
      window.removeEventListener("resize", compute);
    };
  }, [visibleCards, expandedId, baseMinHeight]);

  return (
    <div className="container py-5">
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 align-items-start">
        {visibleCards.map(({ id, imgUrl, imgUrls, title, body }) => {
          const isLong = (body?.length || 0) > TOGGLE_MIN_CHARS;
          const isOpen = expandedId === id;

          return (
            <div key={id} className="col d-flex">
              <CustomCard
                ref={setRef(id)}
                className="w-100"
                imgUrl={imgUrl}
                imgUrls={imgUrls}
                title={title}
                body={body}
                cardStyle={
                  !isMobile && !isOpen && baseMinHeight
                    ? { minHeight: baseMinHeight }
                    : undefined
                }
                bodyClampLines={isLong && !isOpen ? CLAMP_LINES : undefined}
              >
                {isLong && (
                  <div className="mt-3 text-center">
                    <Button
                      variant="link"
                      className="p-0"
                      style={{
                        color: "#58c459",
                        fontWeight: 500,
                        textDecoration: "none",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.textDecoration = "underline")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.textDecoration = "none")
                      }
                      onClick={() => toggle(id)}
                      aria-expanded={isOpen}
                    >
                      {isOpen ? t("common.viewLess", "View Less") : t("common.viewMore", "View More")}
                    </Button>
                  </div>
                )}
              </CustomCard>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdoptableDog;
